import { Routes } from '@angular/router';
import { authGuard, noAuthGuard } from '@core/guards/auth.guard';

/**
 * Top-level route table.
 *
 * Mirrors the storyboard's navigation topology: unauthenticated users
 * land on /login with register / reset-password / resend-activation /
 * legal as sibling pages; authenticated users land on /tabs which hosts
 * the four main tabs (wizard, conditions, my-catches, fish-limits) plus
 * a more menu. Secondary screens pushed from within a tab (add-catch,
 * catch-detail, catch-map, tide-graph, etc.) will be added as children
 * of /tabs in later slices.
 */
export const routes: Routes = [
  {
    path: '',
    redirectTo: 'tabs',
    pathMatch: 'full',
  },
  {
    path: 'login',
    canActivate: [noAuthGuard],
    loadComponent: () =>
      import('./pages/login/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'register',
    canActivate: [noAuthGuard],
    loadComponent: () =>
      import('./pages/register/register.page').then((m) => m.RegisterPage),
  },
  {
    path: 'reset-password',
    canActivate: [noAuthGuard],
    loadComponent: () =>
      import('./pages/reset-password/reset-password.page').then(
        (m) => m.ResetPasswordPage,
      ),
  },
  {
    path: 'resend-activation',
    canActivate: [noAuthGuard],
    loadComponent: () =>
      import('./pages/resend-activation/resend-activation.page').then(
        (m) => m.ResendActivationPage,
      ),
  },
  {
    path: 'legal',
    loadComponent: () =>
      import('./pages/legal/legal.page').then((m) => m.LegalPage),
  },
  {
    path: 'about',
    loadComponent: () =>
      import('./pages/about/about.page').then((m) => m.AboutPage),
  },
  {
    path: 'tabs',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/tabs/tabs.page').then((m) => m.TabsPage),
    children: [
      {
        path: '',
        redirectTo: 'wizard',
        pathMatch: 'full',
      },
      {
        path: 'wizard',
        loadComponent: () =>
          import('./pages/wizard/wizard.page').then((m) => m.WizardPage),
      },
      {
        path: 'conditions',
        loadComponent: () =>
          import('./pages/conditions/conditions.page').then(
            (m) => m.ConditionsPage,
          ),
      },
      {
        path: 'my-catches',
        loadComponent: () =>
          import('./pages/my-catches/my-catches.page').then(
            (m) => m.MyCatchesPage,
          ),
      },
      {
        path: 'add-catch',
        loadComponent: () =>
          import('./pages/add-catch/add-catch.page').then(
            (m) => m.AddCatchPage,
          ),
      },
      {
        path: 'catch-detail/:id',
        loadComponent: () =>
          import('./pages/catch-detail/catch-detail.page').then(
            (m) => m.CatchDetailPage,
          ),
      },
      {
        path: 'catch-map/:id',
        loadComponent: () =>
          import('./pages/catch-map/catch-map.page').then(
            (m) => m.CatchMapPage,
          ),
      },
      {
        path: 'fish-limits',
        loadComponent: () =>
          import('./pages/fish-limits/fish-limits.page').then(
            (m) => m.FishLimitsPage,
          ),
      },
      {
        path: 'more',
        loadComponent: () =>
          import('./pages/more/more.page').then((m) => m.MorePage),
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'tabs',
  },
];
